/**
 * MCN Platform - Complete Configuration
 * Environment-aware with multi-tenant and Cloudflare R2 support
 * Version: 2.0.0
 * Last Updated: October 6, 2025
 */

// ============================================================================
// ENVIRONMENT DETECTION
// ============================================================================
const isDevelopment = process.env.NODE_ENV === 'development';
const isProduction = process.env.NODE_ENV === 'production';

// ============================================================================
// CLOUDFLARE R2 CONFIGURATION (Future Implementation)
// ============================================================================
const R2_CONFIG = {
  enabled: process.env.REACT_APP_R2_ENABLED === 'true',
  endpoint: process.env.REACT_APP_R2_ENDPOINT || 'https://pub-xxxxx.r2.dev',
  bucketUrl: process.env.REACT_APP_R2_BUCKET_URL || 'https://assets.choicemcn.com',
  landingPagesPath: process.env.REACT_APP_R2_LANDING_PATH || '/landing-pages'
};

// ============================================================================
// TENANT CONFIGURATION
// ============================================================================
const TENANT_CONFIG = {
  // Current tenant - can be dynamically determined from domain
  currentTenant: process.env.REACT_APP_TENANT_ID || 'choice',
  
  // Domain to tenant mapping (for multi-tenant support)
  domainMapping: {
    'choicemcn.com': 'choice',
    'localhost:3000': 'choice',
    // Add more tenants here:
    // 'tenant2.com': 'tenant2',
    // 'tenant3.com': 'tenant3',
  }
};

// ============================================================================
// ENVIRONMENT-SPECIFIC CONFIGURATION
// ============================================================================
const ENV_CONFIG = {
development: {
    // Backend API
    apiBaseUrl: 'http://localhost:5000',
    
    // Landing pages (local in development)
    landingPagePath: '/landing-pages/choice/index.html',
    
    // Feature flags
    enableDebugMode: true,
    enableMockData: false,
  },
  production: {
    // Backend API (Google Cloud Run) - default if REACT_APP_API_URL not set
    apiBaseUrl: 'https://svc.choicemcn.com',

    // Landing pages (R2 in production if enabled, otherwise local)
    landingPagePath: R2_CONFIG.enabled
      ? `${R2_CONFIG.bucketUrl}${R2_CONFIG.landingPagesPath}/${TENANT_CONFIG.currentTenant}/index.html`
      : '/landing-pages/choice/index.html',

    // Feature flags
    enableDebugMode: false,
    enableMockData: false,
  }
};

// ============================================================================
// SELECT CURRENT CONFIGURATION
// ============================================================================
const currentEnv = isDevelopment ? 'development' : 'production';
const currentConfig = ENV_CONFIG[currentEnv];

// API base URL — resolution order:
//   1. REACT_APP_API_URL env var (set in .env.production / .env.development)
//   2. The per-env apiBaseUrl from ENV_CONFIG above
// Hardcoding localhost broke the production deploy; env-driven now.
export const API_BASE_URL =
  process.env.REACT_APP_API_URL || currentConfig.apiBaseUrl;

export const LANDING_PAGE_PATH = process.env.REACT_APP_LANDING_PATH || 
  currentConfig.landingPagePath;

// ============================================================================
// API ENDPOINTS - ALIGNED WITH BACKEND
// ============================================================================
export const API_ENDPOINTS = {
  // =========================================================================
  // AUTHENTICATION ENDPOINTS
  // =========================================================================
  auth: {
    register: `${API_BASE_URL}/api/auth/register`,
    login: `${API_BASE_URL}/api/auth/login`,
    logout: `${API_BASE_URL}/api/auth/logout`,
    refresh: `${API_BASE_URL}/api/auth/refresh`,
    me: `${API_BASE_URL}/api/auth/me`,
    profile: `${API_BASE_URL}/api/auth/profile`,
    checkEmail: `${API_BASE_URL}/api/auth/check-email`,
    verify: `${API_BASE_URL}/api/auth/verify`,
  },
  
  // Aliases for backward compatibility
  login: `${API_BASE_URL}/api/auth/login`,
  register: `${API_BASE_URL}/api/auth/register`,
  profile: `${API_BASE_URL}/api/auth/profile`,
  verify: `${API_BASE_URL}/api/auth/verify`,
  
  // =========================================================================
  // USER MANAGEMENT ENDPOINTS (Admin)
  // =========================================================================
  users: {
    list: `${API_BASE_URL}/api/users`,
    updateRole: `${API_BASE_URL}/api/users/:userId/role`,
  },
  
  // =========================================================================
  // PROJECT ENDPOINTS
  // =========================================================================
  projects: {
    list: `${API_BASE_URL}/api/projects`,
    statistics: `${API_BASE_URL}/api/projects/statistics`,
    bulkStatus: `${API_BASE_URL}/api/projects/bulk-status`,
    details: `${API_BASE_URL}/api/projects/:id`,
    create: `${API_BASE_URL}/api/projects`,
    update: `${API_BASE_URL}/api/projects/:id`,
    delete: `${API_BASE_URL}/api/projects/:id`,
    updates: `${API_BASE_URL}/api/projects/:id/updates`,
    investments: `${API_BASE_URL}/api/projects/:id/investments`,
    questions: `${API_BASE_URL}/api/projects/:id/questions`,
  },
  
  // =========================================================================
  // INVESTMENT ENDPOINTS - FIXED TO MATCH BACKEND
  // =========================================================================
  investments: {
    // ✓ CORRECTED: Backend uses /available-projects
    opportunities: `${API_BASE_URL}/api/investments/available-projects`,
    
    // ✓ CORRECTED: Backend uses /my-investments
    portfolio: `${API_BASE_URL}/api/investments/my-investments`,
    myInvestments: `${API_BASE_URL}/api/investments/my-investments`,
    
    // ✓ CORRECTED: Backend uses POST /api/investments
    invest: `${API_BASE_URL}/api/investments`,
    create: `${API_BASE_URL}/api/investments`,
    
    // Not in backend logs but keeping for future
    withdraw: `${API_BASE_URL}/api/investments/withdraw`,
	 creators: `${API_BASE_URL}/api/creators/investment-opportunities`
  },
  
  // =========================================================================
  // REVENUE SHARE ENDPOINTS
  // =========================================================================
  revenueShare: {
    create: `${API_BASE_URL}/api/revenue-share/create`,
    opportunities: `${API_BASE_URL}/api/revenue-share/opportunities`,
    invest: `${API_BASE_URL}/api/revenue-share/invest`,
    trackRevenue: `${API_BASE_URL}/api/revenue-share/track-revenue`,
    myEarnings: `${API_BASE_URL}/api/revenue-share/my-earnings`,
    verifyChannel: `${API_BASE_URL}/api/revenue-share/verify-channel`,
  },
  
  // =========================================================================
  // CHANNEL MANAGEMENT ENDPOINTS
  // =========================================================================
  channels: {
    list: `${API_BASE_URL}/api/channels`,
    create: `${API_BASE_URL}/api/channels`,
    details: `${API_BASE_URL}/api/channels/:id`,
    updateStatus: `${API_BASE_URL}/api/channels/:id/status`,
    sync: `${API_BASE_URL}/api/channels/:id/sync`,
    assignManager: `${API_BASE_URL}/api/channels/:id/manager`,
    addCreator: `${API_BASE_URL}/api/channels/:id/creators`,
    removeCreator: `${API_BASE_URL}/api/channels/:id/creators/:creatorId`,
    analytics: `${API_BASE_URL}/api/channels/:id/analytics`,
    distributeRevenue: `${API_BASE_URL}/api/channels/:id/distribute-revenue`,
  },
  
  // Creator analytics
  creators: {
    analytics: `${API_BASE_URL}/api/creators/:id/analytics`,
  },
  
  // =========================================================================
  // GOOGLE INTEGRATION ENDPOINTS
  // =========================================================================
  google: {
    auth: `${API_BASE_URL}/api/google/auth`,
    callback: `${API_BASE_URL}/api/google/callback`,
    sheets: {
      syncInvestments: `${API_BASE_URL}/api/google/sheets/sync-investments`,
    },
    calendar: {
      createMeeting: `${API_BASE_URL}/api/google/calendar/create-meeting`,
    },
    gmail: {
      sendNotification: `${API_BASE_URL}/api/google/gmail/send-notification`,
    },
    youtube: {
      revenue: `${API_BASE_URL}/api/google/youtube/revenue/:channelId`,
    },
    drive: {
      uploadDocument: `${API_BASE_URL}/api/google/drive/upload-document`,
    },
  },
  
  // =========================================================================
  // ADMIN ANALYTICS ENDPOINTS
  // =========================================================================
  admin: {
    dashboardStats: `${API_BASE_URL}/api/admin/dashboard-stats`,
    investmentAnalytics: `${API_BASE_URL}/api/admin/investment-analytics`,
    export: `${API_BASE_URL}/api/admin/export/:type`,
  },
  
  // =========================================================================
  // ANALYTICS ENDPOINTS
  // =========================================================================
  analytics: {
    overview: `${API_BASE_URL}/api/analytics/overview`,
    channels: `${API_BASE_URL}/api/analytics/channels`,
    creators: `${API_BASE_URL}/api/analytics/creators`,
    revenue: `${API_BASE_URL}/api/analytics/revenue`,
    // Public endpoint for homepage
    publicOverview: `${API_BASE_URL}/api/analytics/overview/public`,
  },
  
  // =========================================================================
  // APPLICATION ENDPOINTS
  // =========================================================================
  applications: {
    submit: `${API_BASE_URL}/api/applications/submit`,
    status: `${API_BASE_URL}/api/applications/status/:email`,
    list: `${API_BASE_URL}/api/applications/list`,
    review: `${API_BASE_URL}/api/applications/review/:id`,
  },
  
  // =========================================================================
  // TENANT MANAGEMENT ENDPOINTS (Super Admin)
  // =========================================================================
  tenants: {
    list: `${API_BASE_URL}/api/tenants`,
    create: `${API_BASE_URL}/api/tenants`,
    details: `${API_BASE_URL}/api/tenants/:id`,
    update: `${API_BASE_URL}/api/tenants/:id`,
    delete: `${API_BASE_URL}/api/tenants/:id`,
  },
  
  // =========================================================================
  // CONTENT MANAGEMENT ENDPOINTS
  // =========================================================================
  content: {
    pending: `${API_BASE_URL}/api/content/pending`,
    approved: `${API_BASE_URL}/api/content/approved`,
    rejected: `${API_BASE_URL}/api/content/rejected`,
    all: `${API_BASE_URL}/api/content/all`,
    approve: `${API_BASE_URL}/api/content/approve/:id`,
    reject: `${API_BASE_URL}/api/content/reject/:id`,
  },
  
  // =========================================================================
  // AGGREGATOR ENDPOINTS
  // =========================================================================
  aggregator: {
    stats: `${API_BASE_URL}/api/aggregator/stats`,
    referrals: `${API_BASE_URL}/api/aggregator/referrals`,
    commissions: `${API_BASE_URL}/api/aggregator/commissions`,
    tier: `${API_BASE_URL}/api/aggregator/tier`,
    withdraw: `${API_BASE_URL}/api/aggregator/withdraw`,
  },
  
  // =========================================================================
  // FINANCE ENDPOINTS
  // =========================================================================
  finance: {
    payments: `${API_BASE_URL}/api/finance/payments`,
    revenue: `${API_BASE_URL}/api/finance/revenue`,
    transactions: `${API_BASE_URL}/api/finance/transactions`,
    approve: `${API_BASE_URL}/api/finance/approve`,
  },
  
  // =========================================================================
  // SUPPORT ENDPOINTS
  // =========================================================================
  support: {
    tickets: `${API_BASE_URL}/api/support/tickets`,
    create: `${API_BASE_URL}/api/support/create`,
    reply: `${API_BASE_URL}/api/support/reply`,
    close: `${API_BASE_URL}/api/support/close`,
  },
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Replace :param placeholders in endpoint URLs
 * @param {string} endpoint - Endpoint with :param placeholders
 * @param {object} params - Object with parameter values
 * @returns {string} - URL with parameters replaced
 */
export const buildEndpoint = (endpoint, params = {}) => {
  let url = endpoint;
  Object.keys(params).forEach(key => {
    url = url.replace(`:${key}`, params[key]);
  });
  return url;
};

/**
 * Get tenant-specific landing page URL
 * @param {string} tenantId - Tenant identifier
 * @returns {string} - Landing page URL for tenant
 */
export const getTenantLandingPage = (tenantId = TENANT_CONFIG.currentTenant) => {
  if (R2_CONFIG.enabled && isProduction) {
    return `${R2_CONFIG.bucketUrl}${R2_CONFIG.landingPagesPath}/${tenantId}/index.html`;
  }
  return `/landing-pages/${tenantId}/index.html`;
};

/**
 * Get tenant from current domain
 * @returns {string} - Tenant ID
 */
export const getCurrentTenant = () => {
  const hostname = window.location.hostname;
  return TENANT_CONFIG.domainMapping[hostname] || TENANT_CONFIG.currentTenant;
};

// ============================================================================
// HEALTH CHECK & SYSTEM INFO
// ============================================================================
export const HEALTH_CHECK_URL = `${API_BASE_URL}/api/health`;

// ============================================================================
// FEATURE FLAGS
// ============================================================================
export const FEATURES = {
  enableR2Storage: R2_CONFIG.enabled,
  enableDebugMode: currentConfig.enableDebugMode,
  enableMockData: currentConfig.enableMockData,
  enableMultiTenant: process.env.REACT_APP_MULTI_TENANT === 'true',
  enableAnalytics: process.env.REACT_APP_ENABLE_ANALYTICS !== 'false',
  enableNotifications: process.env.REACT_APP_ENABLE_NOTIFICATIONS !== 'false',
};

// ============================================================================
// DEVELOPMENT LOGGING
// ============================================================================
if (isDevelopment) {
  console.log('╔════════════════════════════════════════╗');
  console.log('║     MCN Platform Configuration        ║');
  console.log('╚════════════════════════════════════════╝');
  console.log('Environment:', currentEnv);
  console.log('Backend URL:', API_BASE_URL);
  console.log('Landing Page:', LANDING_PAGE_PATH);
  console.log('Current Tenant:', getCurrentTenant());
  console.log('R2 Enabled:', R2_CONFIG.enabled);
  console.log('Debug Mode:', currentConfig.enableDebugMode);
  console.log('═══════════════════════════════════════════');
}

// ============================================================================
// EXPORTS
// ============================================================================
export default {
  API_BASE_URL,
  LANDING_PAGE_PATH,
  API_ENDPOINTS,
  FEATURES,
  TENANT_CONFIG,
  R2_CONFIG,
  isDevelopment,
  isProduction,
  buildEndpoint,
  getTenantLandingPage,
  getCurrentTenant,
};

// ============================================================================
// FUTURE R2 MIGRATION GUIDE
// ============================================================================
/*
To enable Cloudflare R2 for landing pages:

1. Set up R2 bucket in Cloudflare dashboard
2. Upload landing pages to: /landing-pages/choice/index.html
3. Create public access URL or custom domain
4. Update .env.production:
   REACT_APP_R2_ENABLED=true
   REACT_APP_R2_BUCKET_URL=https://assets.choicemcn.com
   REACT_APP_R2_LANDING_PATH=/landing-pages

5. Structure in R2:
   /landing-pages/
     └── choice/
         ├── index.html
         ├── css/
         ├── js/
         └── img/

6. For new tenants, just add a folder:
   /landing-pages/tenant2/index.html

7. Update TENANT_CONFIG.domainMapping to map domains to tenant folders
*/